import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import { metaFor, type StatusMeta } from "@/lib/adminContent";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
  type Stat,
} from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";

export interface ModuleField {
  key: string;
  label: string;
  type?: "text" | "textarea" | "number" | "select" | "date" | "boolean" | "tags";
  options?: { value: string; label: string }[];
  placeholder?: string;
  maxLength?: number;
}

export interface ModuleFilter {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface ResourceModuleConfig<T extends { id: number }> {
  table: string;
  orderBy?: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  searchPlaceholder: string;
  searchKeys: string[];
  filters?: ModuleFilter[];
  statusMap?: Record<string, StatusMeta>;
  statusKey?: string;
  stats: (rows: T[]) => Stat[];
  columns: Column<T>[];
  fields: ModuleField[];
  createFields?: ModuleField[];
  createLabel: string;
  emptyTitle: string;
  emptyMessage: string;
  newDefaults: Record<string, unknown>;
  readOnly?: boolean;
  rowTitle: (row: T) => string;
  rowFooter?: (row: T) => ReactNode;
  rowExtra?: (row: T) => ReactNode;
}

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: ModuleField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (field.type === "select") {
    return (
      <select
        value={String(value ?? "")}
        onChange={(event) => onChange(event.target.value)}
        className={fieldInput}
      >
        {(field.options || []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "boolean") {
    return (
      <select
        value={value ? "true" : "false"}
        onChange={(event) => onChange(event.target.value === "true")}
        className={fieldInput}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (field.type === "date") {
    return (
      <input
        type="date"
        value={value ? String(value).slice(0, 10) : ""}
        onChange={(event) => onChange(event.target.value || null)}
        className={textInput}
      />
    );
  }
  if (field.type === "number") {
    return (
      <input
        type="number"
        value={value === null || value === undefined || value === "" ? "" : String(value)}
        onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}
        className={textInput}
      />
    );
  }
  if (field.type === "tags") {
    return (
      <input
        type="text"
        value={Array.isArray(value) ? value.join(", ") : String(value ?? "")}
        placeholder={field.placeholder || "Comma, separated, values"}
        onChange={(event) =>
          onChange(
            event.target.value
              .split(",")
              .map((entry) => entry.trim())
              .filter(Boolean),
          )
        }
        className={textInput}
      />
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea
        rows={4}
        maxLength={field.maxLength}
        value={String(value ?? "")}
        placeholder={field.placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={`${textInput} resize-none`}
      ></textarea>
    );
  }
  return (
    <input
      type="text"
      value={String(value ?? "")}
      placeholder={field.placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={textInput}
    />
  );
}

export default function ResourceModule<T extends { id: number }>({
  config,
}: {
  config: ResourceModuleConfig<T>;
}) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterState, setFilterState] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [edit, setEdit] = useState<Record<string, unknown>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>(config.newDefaults);
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<T>(config.table, config.orderBy));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load this data.");
    } finally {
      setLoading(false);
    }
  }, [config.table, config.orderBy]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const openRow = (row: T) => {
    setSelectedId(row.id);
    const next: Record<string, unknown> = {};
    config.fields.forEach((field) => {
      next[field.key] = (row as unknown as Record<string, unknown>)[field.key];
    });
    setEdit(next);
  };

  const saveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateRow(config.table, selected.id, edit);
      setRows((list) => list.map((row) => (row.id === selected.id ? ({ ...row, ...edit } as unknown as T) : row)));
      notify("Changes saved.");
    } catch {
      setError("We couldn't save those changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const createRow = async () => {
    setSaving(true);
    try {
      const created = await insertRow<T>(config.table, draft);
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft(config.newDefaults);
      notify("Created.");
    } catch {
      setError("We couldn't create that record. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    try {
      await deleteRow(config.table, selected.id);
      setRows((list) => list.filter((row) => row.id !== selected.id));
      setSelectedId(null);
      notify("Deleted.");
    } catch {
      setError("We couldn't delete that record. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      const record = row as unknown as Record<string, unknown>;
      for (const filter of config.filters || []) {
        const active = filterState[filter.key] || "all";
        if (active !== "all" && String(record[filter.key] ?? "") !== active) return false;
      }
      if (!query) return true;
      return config.searchKeys.some((key) =>
        String(record[key] ?? "").toLowerCase().includes(query),
      );
    });
  }, [rows, search, filterState, config]);

  const createFields = config.createFields || config.fields;

  return (
    <div>
      <PageHeader
        eyebrow={config.eyebrow}
        title={config.title}
        subtitle={config.subtitle}
        actions={
          !config.readOnly ? (
            <button
              type="button"
              onClick={() => {
                setDraft(config.newDefaults);
                setCreateOpen(true);
              }}
              className={btnPrimary}
            >
              <i className="ri-add-line"></i> {config.createLabel}
            </button>
          ) : undefined
        }
      />

      <StatCards stats={config.stats(rows)} />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder={config.searchPlaceholder} />
          {(config.filters || []).map((filter) => (
            <Select
              key={filter.key}
              label={filter.label}
              value={filterState[filter.key] || "all"}
              onChange={(value) => setFilterState((state) => ({ ...state, [filter.key]: value }))}
              options={[{ value: "all", label: `All ${filter.label.toLowerCase()}` }, ...filter.options]}
            />
          ))}
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} {config.title.toLowerCase()}
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label={`Loading ${config.title.toLowerCase()}…`} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-inbox-line"
            title={rows.length === 0 ? config.emptyTitle : "No matches found"}
            message={config.emptyMessage}
          />
        ) : (
          <DataTable columns={config.columns} rows={filtered} onRowClick={openRow} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected ? config.rowTitle(selected) : "Record"}
        subtitle={config.title}
        footer={
          selected ? (
            <div className="flex flex-wrap items-center gap-2">
              {!config.readOnly ? (
                <>
                  <button type="button" onClick={remove} className={btnGhost}>
                    <i className="ri-delete-bin-line"></i> Delete
                  </button>
                  <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
                    {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                    Save changes
                  </button>
                </>
              ) : config.rowFooter ? (
                config.rowFooter(selected)
              ) : null}
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            {config.statusMap && config.statusKey ? (
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill
                  {...metaFor(config.statusMap, String((selected as unknown as Record<string, unknown>)[config.statusKey] ?? ""))}
                />
              </div>
            ) : null}

            {config.fields.map((field) => (
              <Field key={field.key} label={field.label}>
                {config.readOnly ? (
                  <p className="text-sm text-foreground-800 break-words">
                    {Array.isArray(edit[field.key])
                      ? (edit[field.key] as unknown[]).join(", ")
                      : String(edit[field.key] ?? "—")}
                  </p>
                ) : (
                  <FieldControl
                    field={field}
                    value={edit[field.key]}
                    onChange={(value) => setEdit((state) => ({ ...state, [field.key]: value }))}
                  />
                )}
              </Field>
            ))}

            {config.rowExtra ? config.rowExtra(selected) : null}
          </>
        ) : null}
      </Drawer>

      {!config.readOnly ? (
        <Modal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          title={config.createLabel}
          subtitle={config.title}
          footer={
            <>
              <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
                Cancel
              </button>
              <button type="button" onClick={createRow} disabled={saving} className={btnPrimary}>
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Create
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {createFields.map((field) => (
              <div key={field.key} className={field.type === "textarea" || field.type === "tags" ? "sm:col-span-2" : ""}>
                <TextField label={field.label}>
                  <FieldControl
                    field={field}
                    value={draft[field.key]}
                    onChange={(value) => setDraft((state) => ({ ...state, [field.key]: value }))}
                  />
                </TextField>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}

      {node}
    </div>
  );
}